import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import PinPicker from "@/components/PinPicker";
import { useSiteSettings } from "@/components/SiteGate";
import {
  LayoutDashboard, FileText, Map as MapIcon, Image as ImageIcon, Settings as SettingsIcon,
  Users, LogOut, Plus, Trash2, Pencil, Upload, X, ExternalLink, KeyRound, Eye,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, fileUrl, LOGO_URL } from "@/lib/api";
import { LAYER_TYPES, TYPE_MAP, CATEGORIES, INFRA_TYPES } from "@/lib/mapConfig";

const TABS = [
  { id: "overview", label: "Overview", short: "Home", icon: LayoutDashboard },
  { id: "research", label: "Research", short: "Articles", icon: FileText },
  { id: "map", label: "Map Features", short: "Map", icon: MapIcon },
  { id: "media", label: "Media Library", short: "Media", icon: ImageIcon },
  { id: "integrations", label: "Integrations", short: "Settings", icon: SettingsIcon },
  { id: "admins", label: "Administrators", short: "Admins", icon: Users },
];

export default function Admin() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading, navigate]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#52606D]">Loading…</div>;
  if (!user) return null;

  return (
    <div className="min-h-screen bg-slate-100 flex">
      {/* Sidebar (desktop) */}
      <aside className="hidden lg:flex w-64 bg-[#0B0F17] text-white flex-col fixed h-screen">
        <div className="p-5 border-b border-white/10 flex items-center gap-3">
          <img src={LOGO_URL} alt="DCEPtNZ" className="w-10 h-10 rounded-md" />
          <div className="leading-none">
            <div className="font-mono text-xs font-semibold tracking-[0.18em]">DCEPtNZ</div>
            <div className="text-[10px] text-white/50 mt-1">Admin Portal</div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${tab === t.id ? "bg-white/10 text-white" : "text-white/55 hover:text-white hover:bg-white/5"}`}
                data-testid={`admin-tab-${t.id}`}>
                <Icon size={17} /> {t.label}
              </button>
            );
          })}
        </nav>
        <div className="p-3 border-t border-white/10">
          <a href="/" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-white/55 hover:text-white hover:bg-white/5 mb-1"><ExternalLink size={17} /> View site</a>
          <button onClick={() => { logout(); navigate("/login"); }} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-white/55 hover:text-white hover:bg-white/5" data-testid="admin-logout"><LogOut size={17} /> Sign out</button>
          <div className="mt-3 px-3 text-[11px] text-white/40 truncate">{user.email}</div>
        </div>
      </aside>

      {/* Top bar (mobile) */}
      <header className="lg:hidden fixed top-0 inset-x-0 z-40 bg-[#0B0F17] text-white h-14 px-4 flex items-center justify-between" data-testid="admin-mobile-topbar">
        <div className="flex items-center gap-2.5">
          <img src={LOGO_URL} alt="DCEPtNZ" className="w-8 h-8 rounded" />
          <div className="leading-none">
            <div className="font-mono text-[11px] font-semibold tracking-[0.18em]">DCEPtNZ</div>
            <div className="text-[10px] text-white/50 mt-0.5">Admin Portal</div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <a href="/" className="p-2.5 rounded-lg text-white/70 hover:text-white" aria-label="View site"><ExternalLink size={18} /></a>
          <button onClick={() => { logout(); navigate("/login"); }} className="p-2.5 rounded-lg text-white/70 hover:text-white" aria-label="Sign out" data-testid="admin-mobile-logout"><LogOut size={18} /></button>
        </div>
      </header>

      {/* Bottom tab bar (mobile) */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-[#0B0F17] text-white border-t border-white/10 grid grid-cols-6 pb-[env(safe-area-inset-bottom)]" data-testid="admin-mobile-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => { setTab(t.id); window.scrollTo(0, 0); }}
              className={`flex flex-col items-center justify-center gap-1 py-2.5 text-[9px] font-medium tracking-wide ${tab === t.id ? "text-emerald-400" : "text-white/55"}`}
              data-testid={`admin-mobile-tab-${t.id}`}>
              <Icon size={19} /> <span className="truncate max-w-full px-0.5">{t.short}</span>
            </button>
          );
        })}
      </nav>

      <div className="flex-1 min-w-0 lg:ml-64 px-4 pt-20 pb-28 lg:p-10">
        {tab === "overview" && <Overview setTab={setTab} />}
        {tab === "research" && <ResearchAdmin />}
        {tab === "map" && <MapAdmin />}
        {tab === "media" && <MediaAdmin />}
        {tab === "integrations" && <Integrations />}
        {tab === "admins" && <Admins currentUser={user} />}
      </div>
    </div>
  );
}

const Header = ({ title, subtitle, action }) => (
  <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6 lg:mb-8 border-b border-slate-200 pb-5">
    <div>
      <h1 className="font-serif text-2xl lg:text-3xl text-[#11181C]">{title}</h1>
      {subtitle && <p className="text-[#52606D] text-sm mt-1.5">{subtitle}</p>}
    </div>
    {action && <div className="[&>*]:w-full sm:[&>*]:w-auto [&>label>span]:w-full sm:[&>label>span]:w-auto">{action}</div>}
  </div>
);

const Btn = ({ children, onClick, variant = "primary", type = "button", testid, className = "" }) => {
  const styles = {
    primary: "bg-[#0B0F17] text-white hover:bg-[#1b2230]",
    ghost: "bg-white border border-slate-300 text-[#11181C] hover:bg-slate-50",
    danger: "bg-white border border-red-200 text-red-600 hover:bg-red-50",
    accent: "bg-[#059669] text-white hover:brightness-110",
  };
  return <button type={type} onClick={onClick} data-testid={testid} className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${styles[variant]} ${className}`}>{children}</button>;
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono text-[11px] uppercase tracking-[0.12em] text-[#52606D] mb-1.5 block">{label}</span>
    {children}
  </label>
);
const inputCls = "w-full px-3.5 py-2.5 rounded-lg border border-slate-300 bg-white text-base sm:text-sm focus:outline-none focus:border-[#059669]";

const Modal = ({ children, onClose, wide }) => (
  <div className="fixed inset-0 z-[2000] bg-black/50 flex items-start justify-center sm:p-4 overflow-y-auto" onClick={onClose}>
    <div className={`bg-white w-full min-h-full sm:min-h-0 sm:rounded-xl ${wide ? "sm:max-w-3xl" : "sm:max-w-xl"} sm:my-8 shadow-2xl`} onClick={(e) => e.stopPropagation()}>
      {children}
    </div>
  </div>
);

/* ---------------- Overview ---------------- */
function Overview({ setTab }) {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/stats").then((r) => setStats(r.data)); }, []);
  const cards = [
    { label: "Published articles", value: stats?.articles, tab: "research" },
    { label: "Draft articles", value: stats?.drafts, tab: "research" },
    { label: "Tracked locations", value: stats?.tracked_locations, tab: "map" },
    { label: "Confirmed centres", value: stats?.confirmed, tab: "map" },
  ];
  return (
    <div>
      <Header title="Overview" subtitle="A snapshot of the DCEPtNZ research record." />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c, i) => (
          <button key={i} onClick={() => setTab(c.tab)} className="bg-white border border-slate-200 rounded-xl p-4 sm:p-6 text-left hover:border-slate-400 transition-colors">
            <div className="font-serif text-3xl lg:text-4xl text-[#11181C]">{c.value ?? "—"}</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-[#52606D] mt-2">{c.label}</div>
          </button>
        ))}
      </div>
      <div className="mt-8 grid md:grid-cols-3 gap-4">
        <QuickCard title="Write an article" desc="Publish research, upload photos, videos and documents." onClick={() => setTab("research")} />
        <QuickCard title="Add a map feature" desc="Plot a data centre, cable, grid line or solar farm." onClick={() => setTab("map")} />
        <QuickCard title="Connect YouTube & Facebook" desc="Link your channel and page in Integrations." onClick={() => setTab("integrations")} />
      </div>
    </div>
  );
}
const QuickCard = ({ title, desc, onClick }) => (
  <button onClick={onClick} className="bg-[#0B0F17] text-white rounded-xl p-6 text-left hover:bg-[#161c28] transition-colors dcep-grain">
    <h3 className="font-serif text-lg">{title}</h3>
    <p className="text-sm text-white/55 mt-2 leading-relaxed">{desc}</p>
  </button>
);

/* ---------------- Research ---------------- */
const emptyPost = { title: "", excerpt: "", body: "", category: "Commentary", status: "published", region: "", infrastructure_type: "", read_minutes: 5, cover_image_url: "", attachments: [], linked_marker_ids: [], tags: [] };

function ResearchAdmin() {
  const [posts, setPosts] = useState([]);
  const [markers, setMarkers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = useCallback(() => {
    api.get("/posts", { params: { include_drafts: true } }).then((r) => setPosts(r.data));
    api.get("/markers").then((r) => setMarkers(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Delete this article? This cannot be undone.")) return;
    await api.delete(`/posts/${id}`);
    toast.success("Article deleted");
    load();
  };

  const drafts = posts.filter((p) => p.status === "draft");
  const shown = filter === "all" ? posts : posts.filter((p) => p.status === filter);
  const chips = [["all", "All", posts.length], ["published", "Published", posts.length - drafts.length], ["draft", "Drafts", drafts.length]];

  return (
    <div>
      <Header title="Research & Articles" subtitle="Create, edit and publish research. Drafts are only visible here, never on the public site." action={<Btn onClick={() => setEditing(emptyPost)} testid="admin-new-post"><Plus size={16} /> New article</Btn>} />
      <div className="flex flex-wrap gap-2 mb-4" data-testid="post-status-filter">
        {chips.map(([id, label, n]) => (
          <button key={id} onClick={() => setFilter(id)} className={`px-3.5 py-1.5 rounded-full text-sm border transition-colors ${filter === id ? "bg-[#0B0F17] text-white border-[#0B0F17]" : "bg-white border-slate-300 text-[#52606D] hover:border-[#0B0F17]"}`} data-testid={`post-filter-${id}`}>
            {label} <span className={`ml-1 font-mono text-xs ${filter === id ? "text-white/60" : "text-[#94A3B8]"}`}>{n}</span>
          </button>
        ))}
      </div>
      <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100">
        {shown.length === 0 && <div className="p-10 text-center text-[#52606D]" data-testid="posts-empty">{filter === "draft" ? "No drafts. Set an article's status to Draft to keep it off the public site while you work on it." : "No articles yet."}</div>}
        {shown.map((p) => (
          <div key={p.id} className="flex flex-wrap items-center gap-3 p-4" data-testid={`admin-post-row-${p.id}`}>
            <div className="flex-1 min-w-[180px]">
              <div className="flex items-center gap-2">
                <span className="font-serif text-lg text-[#11181C] truncate">{p.title}</span>
                <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded-full shrink-0 ${p.status === "published" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{p.status}</span>
              </div>
              <div className="font-mono text-[11px] text-[#52606D] mt-1 truncate">{p.category} · {p.read_minutes || 5} min · /{p.slug}</div>
            </div>
            <div className="flex gap-2 ml-auto">
              <Btn variant="ghost" onClick={() => setEditing(p)} testid={`admin-edit-post-${p.id}`}><Pencil size={15} /> Edit</Btn>
              <Btn variant="danger" onClick={() => remove(p.id)} testid={`admin-delete-post-${p.id}`}><Trash2 size={15} /></Btn>
            </div>
          </div>
        ))}
      </div>
      {editing && <PostEditor post={editing} markers={markers} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function PostEditor({ post, markers, onClose, onSaved }) {
  const [form, setForm] = useState({ ...emptyPost, ...post });
  const [busy, setBusy] = useState(false);
  const [postId, setPostId] = useState(post.id || null);
  const [autosave, setAutosave] = useState({ at: null, dirty: false, saving: false });
  const formRef = useRef(form);
  formRef.current = form;
  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setAutosave((a) => ({ ...a, dirty: true })); };

  const persist = useCallback(async (data, { silent } = {}) => {
    const payload = { ...data, read_minutes: Number(data.read_minutes) || 5 };
    const r = postId ? await api.put(`/posts/${postId}`, payload) : await api.post("/posts", payload);
    if (!postId) setPostId(r.data.id);
    if (!silent) toast.success("Saved");
    return r.data;
  }, [postId]);

  // Auto-save drafts every minute while there are unsaved changes
  const autosaveRef = useRef(autosave);
  autosaveRef.current = autosave;
  useEffect(() => {
    const t = setInterval(async () => {
      const f = formRef.current;
      if (f.status !== "draft" || !f.title.trim() || !autosaveRef.current.dirty || autosaveRef.current.saving) return;
      setAutosave((a) => ({ ...a, saving: true }));
      try {
        await persist(f, { silent: true });
        setAutosave({ at: new Date(), dirty: false, saving: false });
      } catch { setAutosave((a) => ({ ...a, saving: false })); }
    }, 60000);
    return () => clearInterval(t);
  }, [persist]);

  const uploadFile = async (file, asCover = false) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (asCover) set("cover_image_url", data.url);
      else set("attachments", [...(form.attachments || []), { id: data.id, url: data.url, original_filename: data.original_filename, content_type: data.content_type, kind: data.kind }]);
      toast.success("Uploaded");
    } catch { toast.error("Upload failed"); }
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setBusy(true);
    try { await persist(form); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); } finally { setBusy(false); }
  };

  const preview = () => {
    if (!form.title.trim()) { toast.error("Add a title first"); return; }
    localStorage.setItem("dcep-article-preview", JSON.stringify({ ...form, id: postId, published_at: form.published_at || new Date().toISOString() }));
    window.open("/research/preview?preview=local", "_blank", "noopener");
  };

  const close = () => { if (postId && !post.id) onSaved(); else onClose(); };

  const toggleMarker = (id) => set("linked_marker_ids", (form.linked_marker_ids || []).includes(id) ? form.linked_marker_ids.filter((x) => x !== id) : [...(form.linked_marker_ids || []), id]);

  return (
    <Modal onClose={close} wide>
      <div className="flex items-center justify-between p-4 sm:p-5 border-b border-slate-200 sticky top-0 bg-white sm:rounded-t-xl z-10">
        <h2 className="font-serif text-2xl text-[#11181C]">{post.id ? "Edit article" : "New article"}</h2>
        <button onClick={onClose}><X className="text-[#52606D]" /></button>
      </div>
      <div className="p-4 sm:p-6 space-y-5 sm:max-h-[70vh] sm:overflow-y-auto">
        <Field label="Title"><input className={inputCls} value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="post-title-input" /></Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Category">
            <select className={inputCls} value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="post-category-select">
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Status">
            <select className={inputCls} value={form.status} onChange={(e) => set("status", e.target.value)} data-testid="post-status-select">
              <option value="published">Published</option>
              <option value="draft">Draft</option>
            </select>
          </Field>
          <Field label="Infrastructure theme">
            <select className={inputCls} value={form.infrastructure_type} onChange={(e) => set("infrastructure_type", e.target.value)}>
              <option value="">—</option>
              {INFRA_TYPES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Region"><input className={inputCls} value={form.region} onChange={(e) => set("region", e.target.value)} /></Field>
        </div>
        <Field label="Excerpt"><textarea rows={2} className={inputCls} value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} data-testid="post-excerpt-input" /></Field>
        <Field label="Body (Markdown supported)"><textarea rows={10} className={`${inputCls} font-mono text-[13px]`} value={form.body} onChange={(e) => set("body", e.target.value)} data-testid="post-body-input" /></Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Read time (minutes)"><input type="number" className={inputCls} value={form.read_minutes} onChange={(e) => set("read_minutes", e.target.value)} /></Field>
          <Field label="Cover image">
            <div className="flex items-center gap-3">
              {form.cover_image_url && <img src={fileUrl(form.cover_image_url)} alt="" className="w-12 h-12 rounded object-cover border border-slate-200" />}
              <label className="cursor-pointer"><span className={`${inputCls} inline-flex items-center gap-2 w-auto`}><Upload size={15} /> Upload</span>
                <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadFile(e.target.files[0], true)} data-testid="post-cover-upload" /></label>
              {form.cover_image_url && <button onClick={() => set("cover_image_url", "")} className="text-red-500 text-xs">Remove</button>}
            </div>
          </Field>
        </div>

        {/* Attachments */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono text-[11px] uppercase tracking-[0.12em] text-[#52606D]">Attachments (photos, videos, files)</span>
            <label className="cursor-pointer"><span className="inline-flex items-center gap-1.5 text-sm text-[#059669] font-medium"><Upload size={14} /> Add file</span>
              <input type="file" className="hidden" onChange={(e) => e.target.files[0] && uploadFile(e.target.files[0])} data-testid="post-attachment-upload" /></label>
          </div>
          <div className="flex flex-wrap gap-2">
            {(form.attachments || []).map((a) => (
              <div key={a.id} className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-100 border border-slate-200 text-xs">
                <span className="text-[#059669] uppercase font-mono text-[9px]">{a.kind}</span>
                <span className="text-[#11181C] max-w-[140px] truncate">{a.original_filename}</span>
                <button onClick={() => set("attachments", form.attachments.filter((x) => x.id !== a.id))}><X size={13} className="text-[#52606D]" /></button>
              </div>
            ))}
            {(form.attachments || []).length === 0 && <span className="text-xs text-[#64748B] italic">No attachments yet.</span>}
          </div>
        </div>

        {/* Linked markers */}
        <div>
          <span className="font-mono text-[11px] uppercase tracking-[0.12em] text-[#52606D] mb-2 block">Link to map features</span>
          <div className="flex flex-wrap gap-2">
            {markers.map((m) => {
              const on = (form.linked_marker_ids || []).includes(m.id);
              return (
                <button key={m.id} onClick={() => toggleMarker(m.id)} className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${on ? "bg-[#059669] text-white border-[#059669]" : "bg-white border-slate-200 text-[#52606D]"}`}>
                  {m.name}
                </button>
              );
            })}
            {markers.length === 0 && <span className="text-xs text-[#64748B] italic">No map features yet.</span>}
          </div>
        </div>
      </div>
      <div className="p-4 sm:p-5 border-t border-slate-200 flex flex-wrap items-center gap-3 sticky bottom-0 bg-white sm:rounded-b-xl">
        <div className="w-full sm:w-auto sm:flex-1 text-xs text-[#64748B]" data-testid="post-autosave-status">
          {form.status === "draft"
            ? (autosave.saving ? "Saving draft…" : autosave.at ? `Draft auto-saved at ${autosave.at.toLocaleTimeString("en-NZ", { hour: "2-digit", minute: "2-digit" })}` : "Drafts auto-save every minute while you write.")
            : "Auto-save is only on for drafts. Switch status to Draft to enable it."}
        </div>
        <div className="flex gap-2 w-full sm:w-auto [&>button]:flex-1 sm:[&>button]:flex-none">
          <Btn variant="ghost" onClick={close} testid="post-cancel-button">Cancel</Btn>
          <Btn variant="ghost" onClick={preview} testid="post-preview-button"><Eye size={15} /> Preview</Btn>
          <Btn variant="accent" onClick={save} testid="post-save-button">{busy ? "Saving…" : "Save article"}</Btn>
        </div>
      </div>
    </Modal>
  );
}

/* ---------------- Map features ---------------- */
const emptyMarker = { name: "", type: "confirmed_dc", geometry: "point", lat: "", lng: "", coordinates: [], region: "", operator: "", capacity: "", status_label: "", description: "", linked_post_ids: [] };

function MapAdmin() {
  const [markers, setMarkers] = useState([]);
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    api.get("/markers").then((r) => setMarkers(r.data));
    api.get("/posts", { params: { include_drafts: true } }).then((r) => setPosts(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Delete this map feature?")) return;
    await api.delete(`/markers/${id}`);
    toast.success("Feature deleted");
    load();
  };

  return (
    <div>
      <Header title="Map Features" subtitle="Manage data centres, cables, grid lines and renewables." action={<Btn onClick={() => setEditing(emptyMarker)} testid="admin-new-marker"><Plus size={16} /> New feature</Btn>} />
      <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100">
        {markers.length === 0 && <div className="p-10 text-center text-[#52606D]">No map features yet.</div>}
        {markers.map((m) => {
          const t = TYPE_MAP[m.type];
          return (
            <div key={m.id} className="flex flex-wrap items-center gap-3 p-4" data-testid={`admin-marker-row-${m.id}`}>
              <span className="w-3 h-3 rounded-full shrink-0" style={{ background: t?.color }} />
              <div className="flex-1 min-w-[160px]">
                <div className="font-serif text-lg text-[#11181C] truncate">{m.name}</div>
                <div className="font-mono text-[11px] text-[#52606D] mt-0.5 truncate">{t?.name} · {m.geometry} · {[m.operator, m.region].filter(Boolean).join(" · ")}</div>
              </div>
              <div className="flex gap-2 ml-auto">
                <Btn variant="ghost" onClick={() => setEditing(m)} testid={`admin-edit-marker-${m.id}`}><Pencil size={15} /> Edit</Btn>
                <Btn variant="danger" onClick={() => remove(m.id)} testid={`admin-delete-marker-${m.id}`}><Trash2 size={15} /></Btn>
              </div>
            </div>
          );
        })}
      </div>
      {editing && <MarkerEditor marker={editing} posts={posts} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function MarkerEditor({ marker, posts, onClose, onSaved }) {
  const [form, setForm] = useState({ ...emptyMarker, ...marker, coordsText: (marker.coordinates || []).map((c) => c.join(", ")).join("\n") });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isLine = form.geometry === "line";

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      const payload = {
        name: form.name, type: form.type, geometry: form.geometry, region: form.region,
        operator: form.operator, capacity: form.capacity, status_label: form.status_label,
        description: form.description, linked_post_ids: form.linked_post_ids || [],
        lat: isLine ? null : parseFloat(form.lat), lng: isLine ? null : parseFloat(form.lng),
        coordinates: isLine ? (form.coordsText || "").split("\n").map((l) => l.split(",").map((n) => parseFloat(n.trim()))).filter((c) => c.length === 2 && !isNaN(c[0]) && !isNaN(c[1])) : [],
      };
      if (marker.id) await api.put(`/markers/${marker.id}`, payload);
      else await api.post("/markers", payload);
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); } finally { setBusy(false); }
  };

  const togglePost = (id) => set("linked_post_ids", (form.linked_post_ids || []).includes(id) ? form.linked_post_ids.filter((x) => x !== id) : [...(form.linked_post_ids || []), id]);

  return (
    <Modal onClose={onClose} wide>
      <div className="flex items-center justify-between p-4 sm:p-5 border-b border-slate-200 sticky top-0 bg-white sm:rounded-t-xl z-10">
        <h2 className="font-serif text-2xl text-[#11181C]">{marker.id ? "Edit feature" : "New feature"}</h2>
        <button onClick={onClose}><X className="text-[#52606D]" /></button>
      </div>
      <div className="p-4 sm:p-6 space-y-5 sm:max-h-[70vh] sm:overflow-y-auto">
        <Field label="Name"><input className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="marker-name-input" /></Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Layer type">
            <select className={inputCls} value={form.type} onChange={(e) => { const t = TYPE_MAP[e.target.value]; set("type", e.target.value); set("geometry", t?.geometry || "point"); }} data-testid="marker-type-select">
              {LAYER_TYPES.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </Field>
          <Field label="Geometry">
            <select className={inputCls} value={form.geometry} onChange={(e) => set("geometry", e.target.value)} data-testid="marker-geometry-select">
              <option value="point">Point (single location)</option>
              <option value="line">Line (route / cable / grid)</option>
            </select>
          </Field>
        </div>
        {isLine ? (
          <Field label="Coordinates — one 'lat, lng' per line (or tap the map to add points)">
            <textarea rows={5} className={`${inputCls} font-mono text-[13px]`} placeholder={"-36.8485, 174.7633\n-37.6, 170.5"} value={form.coordsText} onChange={(e) => set("coordsText", e.target.value)} data-testid="marker-coords-input" />
          </Field>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Latitude"><input className={inputCls} value={form.lat} onChange={(e) => set("lat", e.target.value)} placeholder="-36.8485" data-testid="marker-lat-input" /></Field>
            <Field label="Longitude"><input className={inputCls} value={form.lng} onChange={(e) => set("lng", e.target.value)} placeholder="174.7633" data-testid="marker-lng-input" /></Field>
          </div>
        )}
        <PinPicker
          color={TYPE_MAP[form.type]?.color}
          isLine={isLine}
          lat={parseFloat(form.lat)} lng={parseFloat(form.lng)}
          coords={(form.coordsText || "").split("\n").map((l) => l.split(",").map((n) => parseFloat(n.trim()))).filter((c) => c.length === 2 && !isNaN(c[0]) && !isNaN(c[1]))}
          onPick={(lat, lng) => {
            if (isLine) set("coordsText", `${form.coordsText ? form.coordsText.replace(/\s+$/, "") + "\n" : ""}${lat}, ${lng}`);
            else { set("lat", lat); set("lng", lng); }
          }}
          onClearLast={isLine ? () => set("coordsText", (form.coordsText || "").trim().split("\n").slice(0, -1).join("\n")) : null}
        />
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Operator"><input className={inputCls} value={form.operator} onChange={(e) => set("operator", e.target.value)} /></Field>
          <Field label="Capacity (e.g. 40MW — sets the dot size on the map)"><input className={inputCls} value={form.capacity} onChange={(e) => set("capacity", e.target.value)} placeholder="40MW" data-testid="marker-capacity-input" /></Field>
          <Field label="Region"><input className={inputCls} value={form.region} onChange={(e) => set("region", e.target.value)} /></Field>
          <Field label="Status label"><input className={inputCls} value={form.status_label} onChange={(e) => set("status_label", e.target.value)} placeholder="Confirmed / Under development" /></Field>
        </div>
        <Field label="Description"><textarea rows={3} className={inputCls} value={form.description} onChange={(e) => set("description", e.target.value)} /></Field>
        <div>
          <span className="font-mono text-[11px] uppercase tracking-[0.12em] text-[#52606D] mb-2 block">Link to research articles</span>
          <div className="flex flex-wrap gap-2">
            {posts.map((p) => {
              const on = (form.linked_post_ids || []).includes(p.id);
              return <button key={p.id} onClick={() => togglePost(p.id)} className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${on ? "bg-[#059669] text-white border-[#059669]" : "bg-white border-slate-200 text-[#52606D]"}`}>{p.title}</button>;
            })}
            {posts.length === 0 && <span className="text-xs text-[#64748B] italic">No articles yet.</span>}
          </div>
        </div>
      </div>
      <div className="p-4 sm:p-5 border-t border-slate-200 flex justify-end gap-3 sticky bottom-0 bg-white sm:rounded-b-xl [&>button]:flex-1 sm:[&>button]:flex-none">
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn variant="accent" onClick={save} testid="marker-save-button">{busy ? "Saving…" : "Save feature"}</Btn>
      </div>
    </Modal>
  );
}

/* ---------------- Media ---------------- */
function MediaAdmin() {
  const [files, setFiles] = useState([]);
  const load = useCallback(() => api.get("/media").then((r) => setFiles(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try { await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Uploaded"); load(); }
    catch { toast.error("Upload failed"); }
  };
  const remove = async (id) => { await api.delete(`/media/${id}`); toast.success("Removed"); load(); };

  return (
    <div>
      <Header title="Media Library" subtitle="All uploaded photos, videos and documents." action={
        <label className="cursor-pointer"><span className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium bg-[#0B0F17] text-white"><Upload size={16} /> Upload file</span>
          <input type="file" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} data-testid="media-upload" /></label>
      } />
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {files.length === 0 && <div className="col-span-full p-10 text-center text-[#52606D] bg-white border border-slate-200 rounded-xl">No media uploaded yet.</div>}
        {files.map((f) => (
          <div key={f.id} className="bg-white border border-slate-200 rounded-xl overflow-hidden group" data-testid={`media-item-${f.id}`}>
            <div className="aspect-square bg-slate-100 flex items-center justify-center overflow-hidden">
              {f.kind === "image" ? <img src={fileUrl(f.url)} alt={f.original_filename} className="w-full h-full object-cover" />
                : f.kind === "video" ? <video src={fileUrl(f.url)} className="w-full h-full object-cover" />
                : <FileText size={36} className="text-[#52606D]" />}
            </div>
            <div className="p-3 flex items-center justify-between gap-2">
              <span className="text-xs text-[#11181C] truncate">{f.original_filename}</span>
              <button onClick={() => remove(f.id)} className="text-red-500 shrink-0"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Site visibility ---------------- */
function SiteVisibility({ s, set }) {
  const { refresh } = useSiteSettings();
  const isPublic = s.site_public !== false;
  const [msg, setMsg] = useState(s.coming_soon_message || "");
  const [busy, setBusy] = useState(false);
  const update = async (payload, ok) => {
    setBusy(true);
    try {
      const { data } = await api.put("/settings", payload);
      set("site_public", data.site_public !== false);
      set("coming_soon_message", data.coming_soon_message || "");
      refresh();
      toast.success(ok);
    } catch { toast.error("Save failed"); } finally { setBusy(false); }
  };
  return (
    <div className={`border rounded-xl p-4 sm:p-6 space-y-4 ${isPublic ? "bg-white border-slate-200" : "bg-amber-50 border-amber-300"}`} data-testid="site-visibility-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-serif text-xl text-[#11181C]">Site visibility</h3>
          <p className="text-sm text-[#52606D] mt-1" data-testid="site-visibility-status">
            {isPublic ? "The website is live and visible to everyone." : "Hidden — visitors see a Coming Soon page. Signed-in administrators can still browse and edit everything."}
          </p>
        </div>
        <span className={`font-mono text-[10px] uppercase tracking-[0.12em] px-2.5 py-1 rounded-full ${isPublic ? "bg-emerald-100 text-emerald-800" : "bg-amber-200 text-amber-900"}`}>{isPublic ? "Public" : "Coming soon"}</span>
      </div>
      <div className="flex flex-wrap gap-2">
        <Btn variant={isPublic ? "accent" : "ghost"} onClick={() => !isPublic && update({ site_public: true }, "Website is now public")} testid="site-visibility-public">Public</Btn>
        <Btn variant={!isPublic ? "accent" : "ghost"} onClick={() => isPublic && update({ site_public: false }, "Website hidden behind Coming Soon page")} testid="site-visibility-hidden">Coming soon (hidden)</Btn>
      </div>
      <Field label="Coming Soon message (optional)">
        <textarea rows={2} className={inputCls} value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="The DCEPtNZ research archive is being prepared and will open to the public shortly." data-testid="site-visibility-message" />
      </Field>
      <Btn variant="ghost" onClick={() => update({ coming_soon_message: msg }, "Message saved")} testid="site-visibility-message-save">{busy ? "Saving…" : "Save message"}</Btn>
    </div>
  );
}

/* ---------------- Integrations ---------------- */
function Integrations() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/settings/admin").then((r) => setS(r.data)); }, []);
  const set = (k, v) => setS((x) => ({ ...x, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/settings", { youtube_channel: s.youtube_channel || "", youtube_api_key: s.youtube_api_key || "", facebook_url: s.facebook_url || "", contact_email: s.contact_email || "", hero_statement: s.hero_statement || "" });
      toast.success("Settings saved");
    } catch { toast.error("Save failed"); } finally { setBusy(false); }
  };
  if (!s) return <div className="text-[#52606D]">Loading…</div>;
  return (
    <div className="max-w-2xl">
      <Header title="Integrations" subtitle="Site visibility, YouTube, Facebook and contact details." />
      <div className="space-y-6">
        <SiteVisibility s={s} set={set} />
        <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-6 space-y-4">
          <h3 className="font-serif text-xl text-[#11181C]">YouTube</h3>
          <Field label="Channel ID, @handle or channel URL"><input className={inputCls} value={s.youtube_channel || ""} onChange={(e) => set("youtube_channel", e.target.value)} placeholder="@DCEPtNZ or UCxxxx…" data-testid="settings-youtube-channel" /></Field>
          <Field label="YouTube Data API v3 key"><input className={inputCls} value={s.youtube_api_key || ""} onChange={(e) => set("youtube_api_key", e.target.value)} placeholder="AIza…" data-testid="settings-youtube-key" /></Field>
          <p className="text-xs text-[#64748B]">Get a free key at console.cloud.google.com → enable "YouTube Data API v3". Latest videos then appear on the Video Media page automatically.</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-6 space-y-4">
          <h3 className="font-serif text-xl text-[#11181C]">Facebook</h3>
          <Field label="Facebook page URL"><input className={inputCls} value={s.facebook_url || ""} onChange={(e) => set("facebook_url", e.target.value)} placeholder="https://www.facebook.com/YourPage" data-testid="settings-facebook-url" /></Field>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-6 space-y-4">
          <h3 className="font-serif text-xl text-[#11181C]">General</h3>
          <Field label="Public contact email"><input className={inputCls} value={s.contact_email || ""} onChange={(e) => set("contact_email", e.target.value)} data-testid="settings-contact-email" /></Field>
        </div>
        <Btn variant="accent" onClick={save} testid="settings-save-button">{busy ? "Saving…" : "Save settings"}</Btn>
      </div>
    </div>
  );
}

/* ---------------- Admins ---------------- */
function ResetPasswordModal({ admin, onClose }) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.put(`/auth/admins/${admin.user_id}/password`, { new_password: pw }); toast.success(`Password reset for ${admin.email}`); onClose(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not reset password"); }
    finally { setBusy(false); }
  };
  return (
    <Modal onClose={onClose}>
      <form onSubmit={submit} className="p-6 space-y-4" data-testid="reset-password-modal">
        <h2 className="font-serif text-2xl text-[#11181C]">Reset password</h2>
        <p className="text-sm text-[#52606D]">Set a new password for <span className="font-medium text-[#11181C]">{admin.email}</span>. They will be signed out everywhere and will need to use the new password. Share it with them privately.</p>
        <Field label="New password (at least 8 characters)"><input type="text" required minLength={8} autoFocus className={inputCls} value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-password-input" /></Field>
        <div className="flex justify-end gap-2">
          <Btn variant="ghost" onClick={onClose} testid="reset-password-cancel">Cancel</Btn>
          <Btn variant="accent" type="submit" testid="reset-password-submit">{busy ? "Saving…" : "Reset password"}</Btn>
        </div>
      </form>
    </Modal>
  );
}

function Admins({ currentUser }) {
  const [admins, setAdmins] = useState([]);
  const [form, setForm] = useState({ email: "", name: "", password: "" });
  const [pw, setPw] = useState("");
  const [resetting, setResetting] = useState(null);
  const load = useCallback(() => api.get("/auth/admins").then((r) => setAdmins(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/admins", form); toast.success("Administrator added"); setForm({ email: "", name: "", password: "" }); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not add administrator"); }
  };
  const remove = async (id) => { if (!window.confirm("Remove this administrator?")) return; try { await api.delete(`/auth/admins/${id}`); toast.success("Removed"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Could not remove"); } };
  const changePw = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/change-password", { new_password: pw }); toast.success("Password updated"); setPw(""); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not update"); }
  };

  return (
    <div className="max-w-2xl">
      <Header title="Administrators" subtitle="Manage who can sign in and administer the site." />
      <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100 mb-6">
        {admins.map((a) => (
          <div key={a.user_id} className="flex flex-wrap items-center gap-3 p-4" data-testid={`admin-user-row-${a.user_id}`}>
            <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center font-mono text-sm text-[#059669] uppercase shrink-0">{(a.name || a.email)[0]}</div>
            <div className="flex-1 min-w-[160px]">
              <div className="text-sm font-medium text-[#11181C]">{a.name || a.email.split("@")[0]}{a.email === currentUser.email && <span className="ml-2 text-[10px] font-mono text-[#059669]">YOU</span>}</div>
              <div className="font-mono text-[11px] text-[#52606D] truncate">{a.email}</div>
            </div>
            {a.user_id !== currentUser.user_id && (
              <div className="flex gap-2 ml-auto">
                <Btn variant="ghost" onClick={() => setResetting(a)} testid={`admin-reset-password-${a.user_id}`}><KeyRound size={15} /> Reset password</Btn>
                <Btn variant="danger" onClick={() => remove(a.user_id)} testid={`admin-remove-${a.user_id}`}><Trash2 size={15} /></Btn>
              </div>
            )}
          </div>
        ))}
      </div>
      {resetting && <ResetPasswordModal admin={resetting} onClose={() => setResetting(null)} />}

      <form onSubmit={add} className="bg-white border border-slate-200 rounded-xl p-4 sm:p-6 space-y-4 mb-6">
        <h3 className="font-serif text-xl text-[#11181C]">Add an administrator</h3>
        <p className="text-xs text-[#64748B]">Only people you add here can sign in. Share the email and password with them privately — they can change their password once signed in.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Email"><input type="email" required className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-admin-email" /></Field>
          <Field label="Name"><input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-admin-name" /></Field>
        </div>
        <Field label="Password (at least 8 characters)"><input type="text" required minLength={8} className={inputCls} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="new-admin-password" /></Field>
        <Btn type="submit" testid="add-admin-button"><Plus size={16} /> Add administrator</Btn>
      </form>

      <form onSubmit={changePw} className="bg-white border border-slate-200 rounded-xl p-4 sm:p-6 space-y-4">
        <h3 className="font-serif text-xl text-[#11181C] flex items-center gap-2"><KeyRound size={18} /> Change your password</h3>
        <Field label="New password (at least 8 characters)"><input type="text" required minLength={8} className={inputCls} value={pw} onChange={(e) => setPw(e.target.value)} data-testid="change-password-input" /></Field>
        <Btn variant="accent" type="submit" testid="change-password-button">Update password</Btn>
      </form>
    </div>
  );
}
